import {useCallback, useEffect, useMemo, useState} from 'react'
import TableLoading from '../../../_metronic/layout/components/Common/TableSkeletonLoading'
import {SHOW_ERROR, SHOW_SUCCESS} from '../../../util'
import {
  HintCard,
  getHintCardsPromptRequest,
  updateHintCardsPromptRequest,
} from '../../../api/endpoints/hintCards'

type Section = {
  key: string
  title: string
  order: number
  editable: boolean
  help?: string
  text: string
}

type PromptDoc = {
  id: number
  version: number
  updated_at?: string
  updated_by?: string
  sections: Section[]
  fallback_cards: {editable?: boolean; help?: string; cards: HintCard[]}
}

const LABEL_MAX = 35
// a short ask, like the label (the agent itself refuses only over 200, then uses its built-in cards and prompt)
const REQUEST_MAX = 60
const CARDS = 4
// an emoji, symbol or exclamation point: the cards never carry one (the agent checks the same)
const hasBadText = (text: string) => text.includes('!') || /[←-￿]|[\uD800-\uDFFF]/.test(text)

const errorMessage = (res: any, fallback: string) => {
  const payload = res?.data ?? res?.response?.data
  const message = payload?.message
  if (typeof message === 'string' && message) return message
  if (message && typeof message === 'object') return Object.values(message).flat().join(', ')
  return fallback
}

const HintCardsPrompt = () => {
  const [doc, setDoc] = useState<PromptDoc | null>(null)
  const [status, setStatus] = useState<'loading' | 'idle' | 'failed'>('loading')
  const [texts, setTexts] = useState<Record<string, string>>({})
  const [cards, setCards] = useState<HintCard[]>([])
  const [saving, setSaving] = useState(false)

  const show = useCallback((d: PromptDoc) => {
    setDoc(d)
    setTexts(Object.fromEntries(d.sections.filter((s) => s.editable).map((s) => [s.key, s.text])))
    setCards((d.fallback_cards?.cards || []).map((c) => ({...c})))
  }, [])

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      show(await getHintCardsPromptRequest())
      setStatus('idle')
    } catch {
      setStatus('failed')
    }
  }, [show])

  useEffect(() => {
    load()
  }, [load])

  const sections = useMemo(
    () => [...(doc?.sections || [])].sort((a, b) => a.order - b.order),
    [doc]
  )

  const errors = useMemo(() => {
    const out: Record<string, string> = {}
    for (const s of sections) {
      if (s.editable && !(texts[s.key] || '').trim()) out[s.key] = 'This section cannot be empty'
    }
    if (cards.length !== CARDS) out.cards = `There must be exactly ${CARDS} cards`
    cards.forEach((c, i) => {
      if (!c.label.trim()) out[`label${i}`] = 'Label is required'
      else if (c.label.trim().length > LABEL_MAX)
        out[`label${i}`] = `At most ${LABEL_MAX} characters`
      else if (hasBadText(c.label)) out[`label${i}`] = 'No emojis or exclamation points'
      if (!c.request.trim()) out[`request${i}`] = 'Request is required'
      else if (c.request.trim().length > REQUEST_MAX)
        out[`request${i}`] = `At most ${REQUEST_MAX} characters`
      else if (hasBadText(c.request)) out[`request${i}`] = 'No emojis or exclamation points'
    })
    return out
  }, [sections, texts, cards])

  const dirty = useMemo(() => {
    if (!doc) return false
    const textChanged = sections.some((s) => s.editable && texts[s.key] !== s.text)
    const before = doc.fallback_cards?.cards || []
    const cardsChanged =
      before.length !== cards.length ||
      cards.some((c, i) => c.label !== before[i]?.label || c.request !== before[i]?.request)
    return textChanged || cardsChanged
  }, [doc, sections, texts, cards])

  const canSave = dirty && Object.keys(errors).length === 0 && !saving

  const onSave = async () => {
    if (!doc) return
    setSaving(true)
    try {
      const res: any = await updateHintCardsPromptRequest(
        {
          base_version: doc.version,
          sections: sections
            .filter((s) => s.editable)
            .map((s) => ({key: s.key, text: texts[s.key]})),
          fallback_cards: cards.map((c) => ({label: c.label.trim(), request: c.request.trim()})),
        },
        doc.id
      )
      const status = res?.status ?? res?.response?.status
      const payload = res?.data ?? res?.response?.data
      if (status >= 200 && status < 300 && payload?.success !== false) {
        SHOW_SUCCESS(true, 'Hint cards prompt saved')
        if (payload?.data?.sections) show(payload.data)
        else load()
      } else if (status === 409) {
        SHOW_ERROR(
          true,
          'Someone saved a newer version meanwhile. Reload the page to see it, then edit again.'
        )
      } else {
        SHOW_ERROR(true, errorMessage(res, 'Failed to save the prompt'))
      }
    } catch {
      SHOW_ERROR(true, 'Failed to save the prompt')
    } finally {
      setSaving(false)
    }
  }

  const onDiscard = () => doc && show(doc)

  // undo this one section's edits: back to the text as it was loaded (the saved version)
  const restoreSection = (s: Section) => setTexts((prev) => ({...prev, [s.key]: s.text}))
  const restoreCards = () => setCards((doc?.fallback_cards?.cards || []).map((c) => ({...c})))
  const cardsChanged =
    !!doc &&
    ((doc.fallback_cards?.cards || []).length !== cards.length ||
      cards.some(
        (c, i) =>
          c.label !== doc.fallback_cards.cards[i]?.label ||
          c.request !== doc.fallback_cards.cards[i]?.request
      ))

  const restoreButton = (onClick: () => void) => (
    <button
      type='button'
      className='btn btn-sm btn-light-primary py-1 px-3 ms-auto'
      onClick={onClick}
      disabled={saving}
      title='Undo your changes to this section'
    >
      <i className='bi bi-arrow-counterclockwise fs-6 me-1'></i>Restore
    </button>
  )

  const lockedPill = (
    <span className='badge badge-secondary text-dark fw-bolder fs-8 py-2 px-3'>
      <i className='bi bi-lock-fill text-dark fs-8 me-1'></i>Locked
    </span>
  )

  const setCard = (i: number, field: keyof HintCard, value: string) =>
    setCards((prev) => prev.map((c, j) => (j === i ? {...c, [field]: value} : c)))

  const rows = (text: string) => Math.min(Math.max(text.split('\n').length + 1, 3), 24)

  const cardsEditor = (
    <div className='mb-8'>
      <div className='d-flex align-items-center gap-2 mb-1'>
        <h4 className='mb-0'>Fallback cards</h4>
        {cardsChanged && restoreButton(restoreCards)}
      </div>
      <div className='text-muted fs-7 mb-3'>
        {doc?.fallback_cards?.help ||
          'Exactly 4 cards for new members. Also used when Gemini fails.'}
      </div>
      {errors.cards && <div className='text-danger mb-2'>{errors.cards}</div>}
      <div className='d-flex flex-column gap-3'>
        {cards.map((c, i) => (
          <div key={i} className='row g-3 align-items-start'>
            <div className='col-md-5'>
              <label className='form-label fs-7 mb-1'>
                Label{' '}
                <span className='text-muted'>
                  ({c.label.trim().length}/{LABEL_MAX})
                </span>
              </label>
              <input
                className={`form-control form-control-solid ${
                  errors[`label${i}`] ? 'is-invalid' : ''
                }`}
                value={c.label}
                onChange={(e) => setCard(i, 'label', e.target.value)}
              />
              {errors[`label${i}`] && (
                <div className='text-danger fs-7 mt-1'>{errors[`label${i}`]}</div>
              )}
            </div>
            <div className='col-md-7'>
              <label className='form-label fs-7 mb-1'>
                Request (sent to the chat when tapped){' '}
                <span className='text-muted'>
                  ({c.request.trim().length}/{REQUEST_MAX})
                </span>
              </label>
              <input
                className={`form-control form-control-solid ${
                  errors[`request${i}`] ? 'is-invalid' : ''
                }`}
                value={c.request}
                onChange={(e) => setCard(i, 'request', e.target.value)}
              />
              {errors[`request${i}`] && (
                <div className='text-danger fs-7 mt-1'>{errors[`request${i}`]}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <>
      <div className='row mb-5'>
        <div className='col-md-8'>
          <h2 className='py-3 mb-0'>Hint cards prompt</h2>
          <div className='text-muted'>
            The instructions Gemini uses to write the 4 suggestion cards shown on a new Howie chat.
          </div>
        </div>
        {doc && (
          <div className='col-md-4 align-self-center text-end text-muted fs-7'>
            Version {doc.version}
            {doc.updated_at ? ` · updated ${String(doc.updated_at).slice(0, 10)}` : ''}
            {doc.updated_by ? ` by ${doc.updated_by}` : ''}
          </div>
        )}
      </div>
      <div className='card mb-5 mb-xl-10 p-5'>
        {status === 'loading' ? (
          <TableLoading />
        ) : status === 'failed' || !doc ? (
          <h2 className='text-center text-danger my-5'>Not able to fetch data at the moment</h2>
        ) : (
          <>
            {sections.map((s) => (
              <div key={s.key}>
                <div className='mb-8'>
                  <div className='d-flex align-items-center gap-2 mb-1'>
                    <h4 className='mb-0'>{s.title}</h4>
                    {!s.editable && lockedPill}
                    {s.editable &&
                      texts[s.key] !== s.text &&
                      restoreButton(() => restoreSection(s))}
                  </div>
                  {s.help && <div className='text-muted fs-7 mb-3'>{s.help}</div>}
                  {s.editable ? (
                    <>
                      <textarea
                        className={`form-control form-control-solid font-monospace fs-7 ${
                          errors[s.key] ? 'is-invalid' : ''
                        }`}
                        rows={rows(texts[s.key] || '')}
                        value={texts[s.key] || ''}
                        onChange={(e) => setTexts((prev) => ({...prev, [s.key]: e.target.value}))}
                      />
                      {errors[s.key] && (
                        <div className='text-danger fs-7 mt-1'>{errors[s.key]}</div>
                      )}
                    </>
                  ) : (
                    <pre
                      className='rounded p-4 mb-0 text-gray-800 fw-semibold fs-7 border border-gray-400'
                      style={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        background: 'var(--bs-gray-200)',
                      }}
                    >
                      {s.text}
                    </pre>
                  )}
                </div>
                {s.key === 'thin_data' && cardsEditor}
              </div>
            ))}
            {!sections.some((s) => s.key === 'thin_data') && cardsEditor}
            <div className='d-flex justify-content-end gap-3 pt-5 border-top'>
              <button
                type='button'
                className='btn btn-light'
                onClick={onDiscard}
                disabled={!dirty || saving}
              >
                Discard changes
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={onSave}
                disabled={!canSave}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default HintCardsPrompt
